import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries } from '@/lib/queryHelpers'
import type { AccountSettings, ApiResponse, PaymentMethodConfig, MaintenanceCategory, ExpenseCategory, CustomerSource, LiffTheme, BookingSettings } from '@/types'

export const accountSettingsKeys = {
  all: ['accountSettings'] as const,
  detail: () => [...accountSettingsKeys.all, 'detail'] as const,
}

export function useAccountSettings() {
  return useQuery({
    queryKey: accountSettingsKeys.detail(),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<AccountSettings>>('/api/account_settings')
      return data.data
    },
  })
}

interface UpdateAccountSettingsInput {
  name?: string
  // EP-160 (section 2.7): top level, not inside settings. A blank string
  // clears the stored value so the default applies again.
  controller_name?: string
  controller_contact?: string
  settings?: {
    timezone?: string
    date_format?: string
    calendar?: string
    currency?: string
    invoice_prefix?: string
    language?: string
    liff_id?: string
    liff_theme?: LiffTheme
    payment_methods?: PaymentMethodConfig
    // EP-136 (T136-026) — account-wide fallback for the booking settings.
    booking_settings?: BookingSettings
    parcel_carriers?: string[]
    maintenance_categories?: MaintenanceCategory[]
    customer_sources?: CustomerSource[]
    expense_categories?: ExpenseCategory[]
    tax_id?: string
    branch_number?: string
    tax_address?: string
    vat_enabled?: boolean
    vat_rate?: number
    lpr_enabled?: boolean
    parking_fee_enabled?: boolean
    condominium_enabled?: boolean
    cmms_enabled?: boolean
    accounting_enabled?: boolean
    late_fees_enabled?: boolean
    announcements_enabled?: boolean
    debt_collection_enabled?: boolean
    microsite_enabled?: boolean
    lead_generation_enabled?: boolean
    line_channel_secret?: string
    // EP-156 T156-002 — the tenant OA's LINE Login channel id (A1).
    line_login_channel_id?: string
    line_channel_access_token?: string
    invoice_qr_image?: string
    onboarding_step?: number
    onboarding_completed?: boolean
    onboarding_property_id?: number | null
    onboarding_customer_id?: number | null
    onboarding_lease_id?: number | null
    onboarding_invoice_id?: number | null
    onboarding_data_import_id?: number | null
    onboarding_data_import_resolver?: Record<string, number>
    onboarding_data_import_cursor?: {
      customerIdx?: number
      leaseIdx?: number
      skipped?: number[]
    }
  }
}

export function useUpdateAccountSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: UpdateAccountSettingsInput) => {
      const { data } = await api.patch<ApiResponse<AccountSettings>>('/api/account_settings', {
        account_settings: input,
      })
      return data.data
    },
    onSuccess: () => {
      // Single-detail resource; .all is the root key covering both .all and .detail() entries.
      removeListQueries(queryClient, accountSettingsKeys.all)
    },
  })
}

/**
 * Fires POST /api/onboarding/complete.
 * Sets account.settings.onboarding_completed = true and onboarding_step = 8.
 * Invalidates accountSettings so subsequent reads reflect the completed state.
 */
export function useCompleteOnboarding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<ApiResponse<AccountSettings>>('/api/onboarding/complete')
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, accountSettingsKeys.all)
    },
  })
}
